/**
 * Gamma LADDER: the largest strikes by absolute gamma, as dashed context lines.
 *
 * The decision levels (walls, zero gamma, IV bands) are NOT drawn here any
 * more: they are price lines with coloured right-axis tags and hover
 * explanations, owned by ChartView's key-levels effect (see utils/keyLevels).
 * Drawing them in both places put two labels on every wall.
 *
 * A primitive rather than price lines on purpose. Price lines are owned by the
 * effects that create them and get recreated on redraw — the same mechanism
 * that let level lines leak back through Clean mode. A primitive is attached
 * once and detaching it is the whole hiding story.
 *
 * Every price here is ALREADY in futures points; the engine converted it. Do
 * not rescale.
 */
import {
  ISeriesPrimitive,
  SeriesAttachedParameter,
  Time,
  IPrimitivePaneRenderer,
  IPrimitivePaneView,
  PrimitivePaneViewZOrder,
} from 'lightweight-charts';
import type { GexLevels, GexElement } from '../../store/gexStore';

/** One line to draw. */
interface GexLine {
  price: number;
  label: string;
  color: string;
  /** Heavier for the decision levels, lighter for ladder context. */
  weight: 'primary' | 'secondary';
}

/**
 * SpotGamma's convention, which traders already read at a glance: call wall is
 * resistance (red), put wall is support (green), zero gamma is the regime
 * boundary (amber).
 */
const COLORS = {
  callWall: '#ef5350',
  putWall: '#26a69a',
  gammaFlip: '#ffa726',
  ladderPositive: 'rgba(239, 83, 80, 0.55)',
  ladderNegative: 'rgba(38, 166, 154, 0.55)',
} as const;

class GexLevelsRenderer implements IPrimitivePaneRenderer {
  private _lines: { y: number; line: GexLine }[] = [];
  private _width = 0;

  update(lines: { y: number; line: GexLine }[], width: number): void {
    this._lines = lines;
    this._width = width;
  }

  draw(target: any): void {
    target.useBitmapCoordinateSpace((scope: any) => {
      const ctx = scope.context as CanvasRenderingContext2D;
      const hRatio = scope.horizontalPixelRatio as number;
      const vRatio = scope.verticalPixelRatio as number;

      for (const { y, line } of this._lines) {
        const py = y * vRatio;
        const primary = line.weight === 'primary';

        ctx.save();
        ctx.strokeStyle = line.color;
        ctx.lineWidth = (primary ? 1.5 : 1) * vRatio;
        // Dashed for the ladder so it cannot be mistaken for a wall at a glance.
        if (!primary) ctx.setLineDash([6 * hRatio, 4 * hRatio]);
        ctx.beginPath();
        ctx.moveTo(0, py);
        ctx.lineTo(this._width * hRatio, py);
        ctx.stroke();
        ctx.restore();

        // Label sits on the left, above the line, so it never collides with the
        // price scale on the right where this chart already crowds.
        ctx.save();
        ctx.font = `${(primary ? 10 : 9) * vRatio}px Arial`;
        ctx.textBaseline = 'bottom';
        ctx.textAlign = 'left';
        const pad = 4 * hRatio;
        const text = line.label;
        const metrics = ctx.measureText(text);
        // Backing plate: these sit over candles, and unbacked text on a chart is
        // unreadable exactly when price is where the level matters.
        ctx.fillStyle = 'rgba(11, 15, 25, 0.72)';
        ctx.fillRect(pad, py - (primary ? 13 : 12) * vRatio, metrics.width + pad * 2, (primary ? 12 : 11) * vRatio);
        ctx.fillStyle = line.color;
        ctx.fillText(text, pad * 2, py - 2 * vRatio);
        ctx.restore();
      }
    });
  }
}

class GexLevelsPaneView implements IPrimitivePaneView {
  constructor(private _source: GexLevelsPrimitive) {}

  zOrder(): PrimitivePaneViewZOrder {
    // Under the candles: these are context, not price action. Drawing over the
    // candles is what makes a chart unreadable.
    return 'bottom';
  }

  renderer(): IPrimitivePaneRenderer {
    return this._source.buildRenderer();
  }
}

export class GexLevelsPrimitive implements ISeriesPrimitive<Time> {
  private _paneView: GexLevelsPaneView;
  private _renderer = new GexLevelsRenderer();
  private _requestUpdate?: () => void;
  private _series?: SeriesAttachedParameter<Time>['series'];
  private _chart?: SeriesAttachedParameter<Time>['chart'];

  private _levels: GexLevels | null = null;
  private _visibility: Record<GexElement, boolean> = {
    callWall: true,
    putWall: true,
    gammaFlip: true,
    ivBands: true,
    strikes: false,
  };
  private _ladderCount = 6;

  constructor() {
    this._paneView = new GexLevelsPaneView(this);
  }

  attached(param: SeriesAttachedParameter<Time>): void {
    this._series = param.series;
    this._chart = param.chart;
    this._requestUpdate = param.requestUpdate;
  }

  detached(): void {
    this._series = undefined;
    this._chart = undefined;
    this._requestUpdate = undefined;
  }

  paneViews(): IPrimitivePaneView[] {
    return [this._paneView];
  }

  updateAllViews(): void {}

  setLevels(levels: GexLevels | null): void {
    this._levels = levels;
    this._requestUpdate?.();
  }

  setVisibility(visibility: Record<GexElement, boolean>, ladderCount: number): void {
    this._visibility = visibility;
    this._ladderCount = ladderCount;
    this._requestUpdate?.();
  }

  /** The lines this primitive would draw, in draw order. Exposed for testing. */
  buildLines(): GexLine[] {
    const l = this._levels;
    if (!l) return [];
    const lines: GexLine[] = [];

    // Ladder first so the walls draw over it where they coincide.
    if (this._visibility.strikes) {
      for (const s of l.strikes.slice(0, this._ladderCount)) {
        lines.push({
          price: s.strike,
          // Magnitude in millions is the readable unit; raw dollars is a wall of digits.
          label: `${(s.gex / 1e6).toFixed(1)}M`,
          color: s.gex >= 0 ? COLORS.ladderPositive : COLORS.ladderNegative,
          weight: 'secondary',
        });
      }
    }

    return lines;
  }

  buildRenderer(): IPrimitivePaneRenderer {
    const series = this._series;
    const chart = this._chart;
    if (!series || !chart) {
      this._renderer.update([], 0);
      return this._renderer;
    }

    const width = chart.timeScale().width();
    const positioned: { y: number; line: GexLine }[] = [];
    for (const line of this.buildLines()) {
      const y = series.priceToCoordinate(line.price);
      // A level off the visible price scale is dropped, not clamped to an edge —
      // clamping would draw a wall hard against the top or bottom where there
      // is none.
      if (y === null) continue;
      positioned.push({ y, line });
    }

    this._renderer.update(positioned, width);
    return this._renderer;
  }
}
